/**
 * AusMed Mega Nav
 *
 * Two panel modes:
 *  - "all-categories": triggered by All Categories button. Shows left col + right tiles (mega_menu).
 *  - "pill": triggered by top nav pills. Shows full-width tile grid for that pill's children.
 */
(function () {
  /** @type {HTMLElement | null} */
  const navEl = document.querySelector('[data-ausmed-mega-nav]');
  if (!navEl) return;
  /** @type {HTMLElement} */
  const nav = navEl;

  /** @type {HTMLElement | null} */
  const allCatsPanel = /** @type {HTMLElement | null} */ (nav.querySelector('[data-ausmed-panel="all-categories"]'));
  /** @type {HTMLElement | null} */
  const pillPanel = /** @type {HTMLElement | null} */ (nav.querySelector('[data-ausmed-panel="pill"]'));
  /** @type {HTMLElement | null} */
  const openButton = /** @type {HTMLElement | null} */ (nav.querySelector('.ausmed-mega-nav__all-categories'));
  const topTriggers = nav.querySelectorAll('[data-ausmed-top-trigger]');
  const categoryLinks = nav.querySelectorAll('[data-ausmed-category-link]');
  const allCatsContents = nav.querySelectorAll('[data-ausmed-panel="all-categories"] .ausmed-mega-panel__content');
  const pillContents = nav.querySelectorAll('[data-ausmed-panel="pill"] .ausmed-mega-panel__content--pill');

  const hideDelay = 300;
  /** @type {number | null} */
  let hideTimeout = null;
  /** @type {'all-categories'|'pill'|null} */
  let activeMode = null;
  let activeIndex = 0;

  function cancelCloseTimer() {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
  }

  function hideBothPanels() {
    allCatsPanel?.classList.remove('is-active');
    pillPanel?.classList.remove('is-active');
  }

  /** @param {HTMLElement | null} panel */
  function showPanel(panel) {
    if (!panel) return;
    panel.classList.add('is-active');
  }

  /** @param {number} index */
  function setAllCatsActive(index) {
    allCatsContents.forEach((c) => {
      const idx = parseInt(c.getAttribute('data-category-index') || '0', 10);
      const active = idx === index;
      c.setAttribute('aria-hidden', active ? 'false' : 'true');
      c.classList.toggle('is-active', active);
    });
    categoryLinks.forEach((link) => {
      const idx = parseInt(link.getAttribute('data-category-index') || '0', 10);
      link.classList.toggle('is-active', idx === index);
    });
  }

  /** @param {number} index */
  function setPillActive(index) {
    pillContents.forEach((c) => {
      const idx = parseInt(c.getAttribute('data-pill-index') || '0', 10);
      const active = idx === index;
      c.setAttribute('aria-hidden', active ? 'false' : 'true');
      c.classList.toggle('is-active', active);
    });
  }

  /** @param {number} [index] */
  function openAllCategories(index) {
    cancelCloseTimer();
    activeMode = 'all-categories';
    activeIndex = typeof index === 'number' ? index : 0;
    hideBothPanels();
    setAllCatsActive(activeIndex);
    showPanel(allCatsPanel);
    nav.classList.add('is-open');
    if (openButton) openButton.setAttribute('aria-expanded', 'true');
    syncTopTriggers();
  }

  /** @param {number} index */
  function openPillPanel(index) {
    cancelCloseTimer();
    activeMode = 'pill';
    activeIndex = index;
    hideBothPanels();
    setPillActive(index);
    showPanel(pillPanel);
    nav.classList.add('is-open');
    if (openButton) openButton.setAttribute('aria-expanded', 'false');
    syncTopTriggers();
  }

  function syncTopTriggers() {
    topTriggers.forEach((el) => {
      const idx = parseInt(el.getAttribute('data-top-menu-index') || '', 10);
      const active = activeMode === 'pill' && idx === activeIndex;
      el.classList.toggle('ausmed-mega-nav__link--panel-active', active);
      el.setAttribute('aria-expanded', active ? 'true' : 'false');
    });
  }

  function closePanel() {
    hideTimeout = setTimeout(() => {
      nav.classList.remove('is-open');
      hideBothPanels();
      if (openButton) openButton.setAttribute('aria-expanded', 'false');
      topTriggers.forEach((el) => {
        el.classList.remove('ausmed-mega-nav__link--panel-active');
        el.setAttribute('aria-expanded', 'false');
      });
      categoryLinks.forEach((l) => l.classList.remove('is-active'));
      activeMode = null;
      hideTimeout = null;
    }, hideDelay);
  }

  function closePanelNow() {
    cancelCloseTimer();
    nav.classList.remove('is-open');
    hideBothPanels();
    if (openButton) openButton.setAttribute('aria-expanded', 'false');
    topTriggers.forEach((el) => {
      el.classList.remove('ausmed-mega-nav__link--panel-active');
      el.setAttribute('aria-expanded', 'false');
    });
    categoryLinks.forEach((l) => l.classList.remove('is-active'));
    activeMode = null;
  }

  // Initialise panels hidden
  hideBothPanels();

  // All Categories button
  if (openButton) {
    openButton.addEventListener('mouseenter', () => openAllCategories(0));
    openButton.addEventListener('focus', () => openAllCategories(0));
    openButton.addEventListener('click', () => {
      if (activeMode === 'all-categories') {
        closePanelNow();
      } else {
        openAllCategories(0);
      }
    });
  }

  // Top nav pills
  topTriggers.forEach((link) => {
    const idx = parseInt(link.getAttribute('data-top-menu-index') || '', 10);
    if (!Number.isFinite(idx)) return;
    link.addEventListener('mouseenter', () => openPillPanel(idx));
    link.addEventListener('focus', () => openPillPanel(idx));
  });

  // Left column category links (all-categories panel)
  categoryLinks.forEach((link) => {
    const idx = parseInt(link.getAttribute('data-category-index') || '0', 10);
    link.addEventListener('mouseenter', () => {
      cancelCloseTimer();
      setAllCatsActive(idx);
    });
    link.addEventListener('focus', () => {
      cancelCloseTimer();
      setAllCatsActive(idx);
    });
  });

  // Mouse leave / enter on bar + panels
  const menuBar = nav.querySelector('.ausmed-mega-nav__bar');

  /** @param {EventTarget | null} target */
  function isInsideNav(target) {
    if (!target || !(target instanceof Node)) return false;
    return (
      menuBar?.contains(target) ||
      allCatsPanel?.contains(target) ||
      pillPanel?.contains(target)
    );
  }

  menuBar?.addEventListener('mouseenter', cancelCloseTimer);
  menuBar?.addEventListener('mouseleave', (e) => {
    if (isInsideNav(/** @type {MouseEvent} */ (e).relatedTarget)) return;
    closePanel();
  });

  allCatsPanel?.addEventListener('mouseenter', cancelCloseTimer);
  allCatsPanel?.addEventListener('mouseleave', (e) => {
    if (isInsideNav(/** @type {MouseEvent} */ (e).relatedTarget)) return;
    closePanel();
  });

  pillPanel?.addEventListener('mouseenter', cancelCloseTimer);
  pillPanel?.addEventListener('mouseleave', (e) => {
    if (isInsideNav(/** @type {MouseEvent} */ (e).relatedTarget)) return;
    closePanel();
  });

  nav.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closePanelNow();
  });

  /* ------------------------------------------------------------------------ */
  /* Desktop rail scrolling (chevrons)                                        */
  /* ------------------------------------------------------------------------ */
  const primaryWrap = /** @type {HTMLElement | null} */ (nav.querySelector('[data-ausmed-primary-wrap]'));
  const primaryScroller = /** @type {HTMLElement | null} */ (nav.querySelector('[data-ausmed-primary-scroller]'));
  const scrollLeftBtn = /** @type {HTMLButtonElement | null} */ (nav.querySelector('[data-ausmed-scroll-left]'));
  const scrollRightBtn = /** @type {HTMLButtonElement | null} */ (nav.querySelector('[data-ausmed-scroll-right]'));

  function clamp01(n) {
    return Math.max(0, Math.min(1, n));
  }

  function updateRailControls() {
    if (!primaryWrap || !primaryScroller || !scrollLeftBtn || !scrollRightBtn) return;
    const maxScroll = primaryScroller.scrollWidth - primaryScroller.clientWidth;
    const canScroll = maxScroll > 2;

    scrollLeftBtn.hidden = !canScroll;
    scrollRightBtn.hidden = !canScroll;
    primaryWrap.classList.toggle('has-scroll', canScroll);
    if (!canScroll) return;

    const leftProgress = clamp01(primaryScroller.scrollLeft / maxScroll);
    scrollLeftBtn.disabled = leftProgress <= 0.01;
    scrollRightBtn.disabled = leftProgress >= 0.99;

    scrollLeftBtn.style.opacity = scrollLeftBtn.disabled ? '0.35' : '1';
    scrollRightBtn.style.opacity = scrollRightBtn.disabled ? '0.35' : '1';
    scrollLeftBtn.style.pointerEvents = scrollLeftBtn.disabled ? 'none' : 'auto';
    scrollRightBtn.style.pointerEvents = scrollRightBtn.disabled ? 'none' : 'auto';
  }

  function scrollRailBy(direction) {
    if (!primaryScroller) return;
    const amount = Math.max(180, Math.floor(primaryScroller.clientWidth * 0.6));
    primaryScroller.scrollBy({ left: direction * amount, behavior: 'smooth' });
  }

  scrollLeftBtn?.addEventListener('click', () => scrollRailBy(-1));
  scrollRightBtn?.addEventListener('click', () => scrollRailBy(1));
  primaryScroller?.addEventListener('scroll', updateRailControls, { passive: true });
  window.addEventListener('resize', updateRailControls);
  // Initial measurement can be wrong until fonts/styles settle.
  updateRailControls();
  requestAnimationFrame(() => requestAnimationFrame(updateRailControls));
  window.addEventListener('load', updateRailControls);
  setTimeout(updateRailControls, 250);
})();
